import React, { useEffect, useState, useMemo } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  TextField,
  InputLabel,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core";
import { Email, Visibility, VisibilityOff, Business, Person } from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { openApi } from "../../services/api";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";

const SignUpSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto!").max(50, "Muito longo!").required("Nome é obrigatório"),
  companyName: Yup.string().min(2, "Muito curto!").max(50, "Muito longo!").required("Nome da empresa é obrigatória"),
  email: Yup.string().email("E-mail inválido").required("E-mail é obrigatório"),
  password: Yup.string()
    .min(8, "A senha deve ter pelo menos 8 caracteres")
    .matches(/[a-z]/, "Deve conter uma letra minúscula")
    .matches(/[A-Z]/, "Deve conter uma letra maiúscula")
    .required("Senha é obrigatória"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'As senhas devem coincidir')
    .required('Confirmação de senha é obrigatória'),
  planId: Yup.string().required('Selecione um plano'),
  termo: Yup.boolean().oneOf([true], "Você deve aceitar os termos para continuar."),
});

const useStyles = makeStyles((theme) => {
  const main = theme.palette.primary.main;
  const dark = theme.palette.primary.dark;
  return {
    '@global': {
      'body': {
        minHeight: '100vh',
        overflowY: 'auto !important',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
        '-ms-overflow-style': 'none',
      },
    },
    root: {
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      background: `linear-gradient(135deg, ${main} 0%, ${dark} 100%)`,
      padding: "clamp(16px, 4vw, 48px) 0",
      boxSizing: "border-box",
    },
    card: {
      backgroundColor: theme.mode === "light" ? "rgba(255,255,255,0.97)" : "rgba(20,20,20,0.97)",
      borderRadius: 22,
      boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
      padding: 0,
      minWidth: 320,
      maxWidth: 800,
      width: "100%",
      //marginTop: "clamp(16px, 6vh, 48px)",
      //marginBottom: "clamp(16px, 6vh, 48px)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      transition: 'box-shadow 0.3s, transform 0.3s',
      [theme.breakpoints.down('sm')]: {
        maxWidth: '99vw',
        minWidth: 0,
        borderRadius: 10,
      },
    },
    cardHeader: {
      textAlign: "center",
      padding: "38px 38px 0 38px",
      background: "none", // Remove faixa cinza
    },
    logoImg: {
      width: "100%",
      maxWidth: "100px",
      height: "auto",
      maxHeight: "100px",
      margin: "0 auto",
      content:
        "url(" +
        (theme.mode === "light"
          ? theme.calculatedLogoLight()
          : theme.calculatedLogoDark()) +
        ")",
    },
    title: {
      fontWeight: 700,
      fontSize: 30,
      color: theme.palette.text.primary,
      marginBottom: 6,
      marginTop: 0,
      fontFamily: 'Poppins',
    },
    subtitle: {
      fontSize: 16,
      color: theme.palette.text.secondary,
      marginBottom: 18,
      marginTop: 0,
      fontFamily: 'Poppins',
    },
    cardContent: {
      padding: 40,
      display: "flex",
      flexDirection: "column",
      gap: 18,
      [theme.breakpoints.down('sm')]: {
        padding: 16,
      },
    },
    formRow: {
      display: 'flex',
      gap: 32,
      flexWrap: 'wrap',
      marginBottom: 8,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: 0,
      },
    },
    formCol: {
      flex: 1,
      minWidth: 260,
      [theme.breakpoints.down('sm')]: {
        minWidth: 0,
      },
    },
    label: {
      color: theme.palette.text.primary,
      fontWeight: 600,
      marginBottom: 4,
      fontSize: 15,
    },
    input: {
      background: theme.mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(40,40,40,0.7)",
      borderRadius: 8,
    },
    rememberMe: {
      marginTop: -8,
      marginBottom: -8,
      '& .MuiFormControlLabel-label': {
        color: theme.mode === 'dark' ? '#fff' : theme.palette.text.primary,
      },
    },
    recoveryLink: {
      color: main,
      textDecoration: "underline",
      fontWeight: 500,
      cursor: "pointer",
      '&:hover': { color: dark },
    },
    bottomLink: {
      marginTop: 28,
      textAlign: "center",
      color: "#2CC992",
      fontSize: 16,
      textShadow: "0 1px 4px rgba(0,0,0,0.10)",
      width: '100%',
      maxWidth: "100vw",
      marginLeft: "auto",
      marginRight: "auto",
      position: "static",
      left: "unset",
      top: "unset",
      transform: "none",
    },
    submit: {
      marginTop: 18,
      borderRadius: 10,
      fontWeight: 700,
      fontSize: 18,
      width: "100%",
      background: main,
      color: "#fff",
      padding: "14px 0",
      boxShadow: "0 2px 8px 0 rgba(0,0,0,0.10)",
      transition: "all 0.2s",
      '&:hover': {
        background: dark,
        transform: "scale(1.01)",
      },
    },
    cardFooter: {
      background: "none", // Remove faixa cinza
      borderTop: "none",
      padding: 18,
      textAlign: "center",
    },
  };
});

const SignUp = () => {
  const [plans, setPlans] = useState([]);
  const { getPlanList } = usePlans();
  const { getPublicSetting } = useSettings();
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [allowSignup, setAllowSignup] = useState(true); // Assume habilitado por padrão
  const colorMode = React.useContext(ColorModeContext);

  // Memoiza o cálculo do logo para evitar recálculos desnecessários
  const logoSrc = useMemo(() => {
    let src = "/logo.png";
    try {
      let candidate = null;
      if (colorMode?.mode === "light" && typeof colorMode?.calculatedLogoLight === "function") {
        candidate = colorMode.calculatedLogoLight();
      } else if (colorMode?.mode === "dark" && typeof colorMode?.calculatedLogoDark === "function") {
        candidate = colorMode.calculatedLogoDark();
      }
      if (typeof candidate === "string" && candidate.trim() && candidate !== "undefined" && candidate !== "null") {
        src = candidate;
      }
    } catch (e) {
      console.warn("Erro ao calcular logo:", e);
    }
    return src;
  }, [colorMode?.mode, colorMode?.calculatedLogoLight, colorMode?.calculatedLogoDark]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const planList = await getPlanList({ listPublic: false });
        setPlans(planList);
      } catch (error) {
        toastError(error);
      }
    };
    
    const checkSignupAllowed = async () => {
      try {
        const signupSetting = await getPublicSetting("allowSignup");
        const isAllowed = signupSetting === "enabled";
        setAllowSignup(isAllowed);
        
        // Se cadastro não estiver permitido, redireciona para login
        if (!isAllowed) {
          history.push("/login");
        }
      } catch (error) {
        console.error("Erro ao verificar configuração de cadastro:", error);
        // Em caso de erro, assume que está habilitado
        setAllowSignup(true);
      }
    };
    
    fetchPlans();
    checkSignupAllowed();
  }, [getPlanList, getPublicSetting, history]);

  const handleSignUp = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    setSubmitError("");
    
    try {
      const response = await openApi.post("/auth/signup", values);
      
      // Verifica diferentes tipos de resposta de sucesso
      const { data } = response;
      
      if (data.success === true) {
        // Sucesso total - usuário criado e email enviado
        if (data.emailSent) {
          toast.success(
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                🎉 Conta criada com sucesso!
              </div>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                Verifique seu email com as instruções de acesso.
              </div>
            </div>, 
            {
              autoClose: 8000,
              position: "top-center",
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              style: {
                backgroundColor: "#f0fff4",
                borderLeft: "4px solid #10b981",
                color: "#065f46"
              }
            }
          );
        } else {
          // Sucesso mas email não foi enviado
          toast.success(
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                ✅ Conta criada com sucesso!
              </div>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                Você já pode fazer login com suas credenciais.
              </div>
            </div>,
            {
              autoClose: 6000,
              position: "top-center",
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              style: {
                backgroundColor: "#f0fff4",
                borderLeft: "4px solid #10b981",
                color: "#065f46"
              }
            }
          );
        }
        
        // Aguarda um pouco para o usuário ver a mensagem
        setTimeout(() => {
          history.push("/login");
        }, 2000);
        
      } else if (data.partialSuccess === true) {
        // Sucesso parcial - usuário criado mas algo falhou
        toast.warning(
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ⚠️ Conta criada com sucesso!
            </div>
            <div style={{ fontSize: '14px', color: '#4a5568' }}>
              Houve um problema no envio do email, mas você pode fazer login normalmente.
            </div>
          </div>,
          {
            autoClose: 10000,
            position: "top-center",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
              backgroundColor: "#fffbeb",
              borderLeft: "4px solid #f59e0b",
              color: "#92400e"
            }
          }
        );
        
        setTimeout(() => {
          history.push("/login");
        }, 3000);
      } else {
        // Fallback para respostas de sucesso sem estrutura específica
        toast.success("✅ Conta criada com sucesso! Redirecionando para login...", {
          autoClose: 4000,
          position: "top-center"
        });
        
        setTimeout(() => {
          history.push("/login");
        }, 2000);
      }
      
    } catch (error) {
      console.error("Erro no cadastro:", error);
      
      // Tratamento específico por tipo de erro
      const status = error.response?.status;
      const errorData = error.response?.data;
      
      if (status === 409) {
        // Email já existe
        setSubmitError("Este email já está cadastrado. Tente fazer login ou use outro email.");
        toast.error("❌ Email já cadastrado", {
          autoClose: 6000,
          position: "top-center"
        });
        
      } else if (status === 207) {
        // Multi-status - sucesso parcial (caso backend implemente)
        toast.warning("⚠️ Conta criada, mas houve problema no envio do email. Você pode fazer login.", {
          autoClose: 8000,
          position: "top-center"
        });
        setTimeout(() => history.push("/login"), 2000);
        
      } else if (status === 400) {
        // Dados inválidos
        const message = errorData?.error || "Verifique os dados informados";
        setSubmitError(message);
        toast.error(`❌ ${message}`, {
          autoClose: 7000,
          position: "top-center"
        });
        
      } else if (status >= 500) {
        // Erro do servidor
        setSubmitError("Problema no servidor. Tente novamente em alguns minutos.");
        toast.error("🚨 Erro no servidor. Tente novamente em alguns minutos.", {
          autoClose: 8000,
          position: "top-center"
        });
        
      } else {
        // Erro genérico
        const message = errorData?.error || errorData?.message || "Erro ao criar conta. Tente novamente.";
        setSubmitError(message);
        toast.error(`❌ ${message}`, {
          autoClose: 6000,
          position: "top-center"
        });
      }
      
      // Log detalhado para debug (apenas em desenvolvimento)
      if (process.env.NODE_ENV === 'development') {
        console.error('Detalhes do erro de cadastro:', {
          status,
          data: errorData,
          message: error.message,
          config: error.config
        });
      }
      
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  // Se cadastro não estiver permitido, não renderiza a página
  if (!allowSignup) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: '#fff',
        fontSize: '18px'
      }}>
        Cadastro não permitido
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={classes.card}
      >
        <CardHeader
          title={<img alt="Logo do sistema" className={classes.logoImg} src={logoSrc} />}
          subheader={
            <>
              <Typography variant="h5" align="center" className={classes.title}>Criar nova conta</Typography>
              <Typography variant="subtitle1" align="center" className={classes.subtitle}>Preencha os dados abaixo para se cadastrar</Typography>
            </>
          }
          className={classes.cardHeader}
        />
        <CardContent className={classes.cardContent}>
          {/* Exibe erro de cadastro se houver */}
          {submitError && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {submitError}
            </Alert>
          )}
          
          <Formik
            initialValues={{
              name: "",
              companyName: "",
              email: "",
              password: "",
              confirmPassword: "",
              planId: "",
              termo: false,
            }}
            validationSchema={SignUpSchema}
            onSubmit={handleSignUp}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form>
                <div className={classes.formRow}>
                  <div className={classes.formCol}>
                    <div className={classes.label}>Nome completo</div>
                    <TextField
                      id="name"
                      name="name"
                      variant="outlined"
                      fullWidth
                      value={values.name}
                      onChange={e => setFieldValue("name", e.target.value)}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      margin="dense"
                      disabled={isSubmitting}
                      InputProps={{
                        className: classes.input,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <div className={classes.label}>Nome da empresa</div>
                    <TextField
                      id="companyName"
                      name="companyName"
                      variant="outlined"
                      fullWidth
                      value={values.companyName}
                      onChange={e => setFieldValue("companyName", e.target.value)}
                      error={touched.companyName && Boolean(errors.companyName)}
                      helperText={touched.companyName && errors.companyName}
                      margin="dense"
                      disabled={isSubmitting}
                      InputProps={{
                        className: classes.input,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <div className={classes.label}>E-mail</div>
                    <TextField
                      id="email"
                      name="email"
                      variant="outlined"
                      fullWidth
                      value={values.email}
                      onChange={e => setFieldValue("email", e.target.value)}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      margin="dense"
                      disabled={isSubmitting}
                      InputProps={{
                        className: classes.input,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <div className={classes.formCol}>
                    <div className={classes.label}>Senha</div>
                    <TextField
                      id="password"
                      name="password"
                      variant="outlined"
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                      onChange={e => setFieldValue("password", e.target.value)}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      margin="dense"
                      disabled={isSubmitting}
                      InputProps={{
                        className: classes.input,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword((v) => !v)}
                              edge="end"
                              size="small"
                              disabled={isSubmitting}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <div className={classes.label}>Confirme a senha</div>
                    <TextField
                      id="confirmPassword"
                      name="confirmPassword"
                      variant="outlined"
                      fullWidth
                      type={showConfirmPassword ? "text" : "password"}
                      value={values.confirmPassword}
                      onChange={e => setFieldValue("confirmPassword", e.target.value)}
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      margin="dense"
                      disabled={isSubmitting}
                      InputProps={{
                        className: classes.input,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={() => setShowConfirmPassword((v) => !v)}
                              edge="end"
                              size="small"
                              disabled={isSubmitting}
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <div className={classes.label}>Plano</div>
                    <TextField
                      select
                      id="planId"
                      name="planId"
                      variant="outlined"
                      fullWidth
                      value={values.planId}
                      onChange={e => setFieldValue("planId", e.target.value)}
                      error={touched.planId && Boolean(errors.planId)}
                      helperText={touched.planId && errors.planId}
                      margin="dense"
                      disabled={isSubmitting}
                      InputProps={{ className: classes.input }}
                    >
                      <MenuItem value="">Selecione um plano</MenuItem>
                      {plans.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                      ))}
                    </TextField>
                  </div>
                </div>
                <FormControlLabel
                  className={classes.rememberMe}
                  style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: 16, marginBottom: 0 }}
                  control={
                    <Checkbox
                      id="termo"
                      name="termo"
                      color="primary"
                      checked={values.termo}
                      onChange={e => setFieldValue("termo", e.target.checked)}
                      disabled={isSubmitting}
                    />
                  }
                  label={<span style={{ fontWeight: 500 }}>Li e aceito os Termos de Uso</span>}
                />
                {touched.termo && errors.termo && <Typography color="error" variant="body2" align="center">{errors.termo}</Typography>}
                <Typography
                  variant="body2"
                  align="center"
                  style={{
                    marginTop: 8,
                    marginBottom: 8,
                    color: theme.palette.type === 'dark' || theme.palette.mode === 'dark' ? '#fff' : '#666',
                  }}
                >
                  Ao criar uma conta, você concorda com nossos{' '}
                  <RouterLink 
                    to="/terms" 
                    style={{ color: '#2CC992', textDecoration: 'underline' }}
                  >
                    Termos de Serviço
                  </RouterLink>.
                </Typography>
                <Button 
                  type="submit" 
                  fullWidth 
                  variant="contained" 
                  color="primary" 
                  className={classes.submit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Criando conta..." : "Criar conta"}
                </Button>
              </Form>
            )}
          </Formik>
        </CardContent>
        <CardActions className={classes.cardFooter}>
          <span
            style={{
              fontSize: 15,
              color: theme.palette.type === 'dark' || theme.palette.mode === 'dark' ? '#fff' : '#666',
              width: '100%',
              textAlign: 'center',
            }}
          >
            Já tem uma conta?{' '}
            <RouterLink to="/login" className={classes.recoveryLink} style={{ textDecoration: 'underline' }}>
              Entrar
            </RouterLink>
          </span>
        </CardActions>
      </motion.div>
    </div>
  );
};

export default SignUp;